import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Typography,
  Collapse,
  Box,
} from '@mui/material';
import { Folder, FolderOpen, Home, ExpandMore, ChevronRight } from '@mui/icons-material';
import { useFolderTree } from '../../hooks/useFolders';

function FolderItem({ node, selectedId, onSelect, level = 0 }) {
  const [open, setOpen] = useState(false);
  const hasChildren = node.subfolders && node.subfolders.length > 0;
  const isSelected = selectedId === node.id;

  return (
    <>
      <ListItemButton
        selected={isSelected}
        onClick={() => onSelect(node.id)}
        sx={{ pl: 2 + level * 2 }}
      >
        {hasChildren ? (
          <ListItemIcon sx={{ minWidth: 28 }} onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
            {open ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
          </ListItemIcon>
        ) : (
          <ListItemIcon sx={{ minWidth: 28 }} />
        )}
        <ListItemIcon sx={{ minWidth: 32 }}>
          {isSelected ? <FolderOpen color="primary" fontSize="small" /> : <Folder fontSize="small" />}
        </ListItemIcon>
        <ListItemText primary={node.name} primaryTypographyProps={{ variant: 'body2' }} />
      </ListItemButton>
      {hasChildren && (
        <Collapse in={open}>
          {node.subfolders.map((child) => (
            <FolderItem
              key={child.id}
              node={child}
              selectedId={selectedId}
              onSelect={onSelect}
              level={level + 1}
            />
          ))}
        </Collapse>
      )}
    </>
  );
}

export default function MoveFileDialog({ open, file, onClose, onConfirm, loading }) {
  const { data: folderTree } = useFolderTree();
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setError('');
    try {
      await onConfirm(file, selectedFolderId);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Errore nello spostamento');
    }
  };

  const handleClose = () => {
    setSelectedFolderId(null);
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Sposta "{file?.original_filename}"
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Seleziona la cartella di destinazione:
        </Typography>

        <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
          <ListItemButton
            selected={selectedFolderId === null}
            onClick={() => setSelectedFolderId(null)}
          >
            <ListItemIcon sx={{ minWidth: 28 }} />
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Home fontSize="small" color={selectedFolderId === null ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Root" primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
          </ListItemButton>

          {folderTree?.map((node) => (
            <FolderItem
              key={node.id}
              node={node}
              selectedId={selectedFolderId}
              onSelect={setSelectedFolderId}
            />
          ))}
        </List>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Annulla</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          Sposta
        </Button>
      </DialogActions>
    </Dialog>
  );
}
